import React, { useState, useEffect, useMemo, useRef } from 'react'

import { paymentMethodTab } from './paymentMethodLabel'

const PaymentMethods = ({
  i18n, paymentMethod, setPaymentMethod, paymentMethods, currencies, disabled,
}) => {
  const [ height, setHeight ] = useState('')
  const wrapper = useRef()
  const scrollArea = useRef()
  const scrollInner = useRef()

  useEffect(() => {
    const measureHeight = () => {
      // ResizeObserver callbacks are async, so one can fire after this
      // component unmounts and React nulls the ref — bail rather than read
      // offsetHeight off null (and emit a bogus calc(undefinedpx …)).
      if (!scrollArea.current) return
      setHeight(`calc(${scrollArea.current.offsetHeight}px - 2rem - var(--scrollbar-height))`)
    }

    const resizeObserver = new ResizeObserver(measureHeight)
    resizeObserver.observe(scrollArea.current)

    return () => resizeObserver.disconnect()
  }, [])

  useEffect(() => {
    const item = scrollArea.current.querySelector(`#${paymentMethod}-tab`).closest('.nav-item')
    const padding = parseInt(getComputedStyle(scrollInner.current).paddingLeft)
    const left = item.offsetLeft - scrollArea.current.scrollLeft
    const right = left + item.offsetWidth

    if (left < padding)
      scrollArea.current.scrollTo({
        left: item.offsetLeft - padding,
        behavior: 'smooth',
      })
    else if (right + padding > scrollArea.current.offsetWidth)
      scrollArea.current.scrollTo({
        left: item.offsetLeft + item.offsetWidth - scrollArea.current.offsetWidth + padding,
        behavior: 'smooth',
      })

    // Fix weird browser glitches that scroll the overflow hidden container
    wrapper.current.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  }, [ paymentMethod ])

  const fiatKey = useMemo(() => {
    if (!paymentMethods.fiat) return

    const keys = Object.keys(paymentMethods.fiat)
    return keys.length == 1
    ? keys[0]
    : currencies.find(c => c.symbol != '$')
    ? 'fiat'
    : 'dollars'
  }, [ paymentMethods.fiat ])

  const tabs = useMemo(() =>
    [ 'wallet', 'fiat', 'daf', 'crypto', 'stock', 'deferred' ]
    .filter(tab => paymentMethods[tab])
  , [ paymentMethods ])

  const hideHeader = useMemo(() =>
    document.documentElement.classList.contains('widget-hide-header')
  , [])

  const setTab = index => {
    const tab = tabs.at(index)
    setPaymentMethod(tab)
    scrollArea.current.querySelector(`#${tab}-tab`).focus()
  }

  const handleKeyDown = e => {
    const keys = [ 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End' ]
    if (!keys.includes(e.key)) return

    e.stopPropagation()
    e.preventDefault()

    switch (e.key) {
    case 'Home': return setTab(0)
    case 'End': return setTab(-1)
    case 'ArrowUp':
    case 'ArrowLeft': return setTab(tabs.indexOf(paymentMethod) - 1)
    case 'ArrowDown':
    case 'ArrowRight': {
      const index = tabs.indexOf(paymentMethod)
      return setTab(index < tabs.length - 1 ? index + 1 : 0)
    }
    }
  }

  return (
    <div className={hideHeader ? 'mt-2 mt-sm-3' : 'mt-sm-n3'} style={{
      position: 'relative',
      zIndex: 1,
    }}>
      <div ref={wrapper} style={{
        overflow: 'hidden',
        marginTop: '-0.5rem',
        transform: 'translateY(0.25rem)',
        height,
      }}>
        <div ref={scrollArea} className="text-nowrap" style={{
          overflowX: 'scroll',
          overscrollBehaviorX: 'contain',
          paddingTop: '0.25rem',
          paddingBottom: '2.25rem',
        }}>
          <div ref={scrollInner} className="d-inline-block px-3 px-sm-4">
            <ul
              className="nav nav-tabs flex-nowrap mx-sm-0"
              style={{ margin: '0 calc(-0.5rem - 1px)' }}
              role="tablist"
            >
              {tabs.map(tab =>
                <li key={tab} className="nav-item" role="presentation">
                  <button
                    type="button"
                    role="tab"
                    className={[
                      'nav-link px-2 px-sm-3',
                      paymentMethod == tab && 'active',
                    ].filter(Boolean).join(' ')}
                    id={`${tab}-tab`}
                    aria-controls={`${tab}-tab-panel`}
                    aria-selected={paymentMethod == tab}
                    disabled={disabled}
                    tabIndex={paymentMethod == tab ? 0 : -1}
                    onKeyDown={handleKeyDown}
                    onClick={() => {
                      if (!disabled) setPaymentMethod(tab)
                    }}
                    {...paymentMethodTab(tab == 'fiat' ? fiatKey : tab, i18n)}
                  />
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentMethods
